import React, { useState, useEffect, useRef } from "react";
import { FaSearch, FaExternalLinkAlt, FaCheckCircle } from "react-icons/fa";
import { supabase } from "../services/supabase";
import { GroqDrawerTrigger } from "../components/GroqDrawerTrigger";
import { useHeaderContext } from "../context/HeaderContext";

// State abbreviation mapping for normalization
const STATE_MAP = {
  // Full names to abbreviations
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
  'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
  'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
  'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
  'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
  'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
  'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
  'district of columbia': 'DC', 'washington dc': 'DC', 'dc': 'DC',
  // Reverse mapping (abbreviations to full names)
  'al': 'AL', 'ak': 'AK', 'az': 'AZ', 'ar': 'AR', 'ca': 'CA',
  'co': 'CO', 'ct': 'CT', 'de': 'DE', 'fl': 'FL', 'ga': 'GA',
  'hi': 'HI', 'id': 'ID', 'il': 'IL', 'in': 'IN', 'ia': 'IA',
  'ks': 'KS', 'ky': 'KY', 'la': 'LA', 'me': 'ME', 'md': 'MD',
  'ma': 'MA', 'mi': 'MI', 'mn': 'MN', 'ms': 'MS', 'mo': 'MO',
  'mt': 'MT', 'ne': 'NE', 'nv': 'NV', 'nh': 'NH', 'nj': 'NJ',
  'nm': 'NM', 'ny': 'NY', 'nc': 'NC', 'nd': 'ND', 'oh': 'OH',
  'ok': 'OK', 'or': 'OR', 'pa': 'PA', 'ri': 'RI', 'sc': 'SC',
  'sd': 'SD', 'tn': 'TN', 'tx': 'TX', 'ut': 'UT', 'vt': 'VT',
  'va': 'VA', 'wa': 'WA', 'wv': 'WV', 'wi': 'WI', 'wy': 'WY'
};

// Normalize location string to canonical form (state abbreviation or title case city)
const normalizeLocation = (location) => {
  if (!location) return "";
  
  // Trim and remove trailing punctuation
  let normalized = location.trim().replace(/[,\.\;\:]+$/, '').trim();
  
  // If location contains a comma (e.g., "Bellingham, WA"), extract just the city part
  if (normalized.includes(',')) {
    normalized = normalized.split(',')[0].trim();
  } else {
    // Check if the last word (space-separated) is a state abbreviation
    const words = normalized.split(/\s+/);
    if (words.length > 1) {
      const lastWord = words[words.length - 1].toLowerCase();
      // Check if last word is a state (2 letters matching our STATE_MAP values)
      if (lastWord.length === 2 && STATE_MAP[lastWord]) {
        // Remove the state word, keep only the city
        normalized = words.slice(0, -1).join(' ');
      }
    }
  }
  
  const lower = normalized.toLowerCase();
  
  // Check if it's a full state name or code in our mapping
  if (STATE_MAP[lower]) {
    return STATE_MAP[lower];
  }
  
  // If it's already a 2-letter code matching a state, keep it uppercase
  if (normalized.length === 2 && /^[A-Za-z]{2}$/.test(normalized)) {
    const upper = normalized.toUpperCase();
    if (Object.values(STATE_MAP).includes(upper)) {
      return upper;
    }
  }
  
  // For city names and other locations, normalize to title case
  return normalized.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

// Get display name for location (show canonical + note if different)
const getLocationDisplayName = (location) => {
  const normalized = normalizeLocation(location);
  if (normalized === location.trim()) {
    return normalized;
  }
  return `${normalized}`;
};

const ProvidersPage = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterApproved, setFilterApproved] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  
  // YOGi integration refs
  const groqDrawerOpenRef = useRef(null);
  const groqSetQueryRef = useRef(null);
  
  // Get HeaderContext for setting YOGi context and location
  const { setGroqContext, setLocation } = useHeaderContext();

  // Get provider summary for YOGi
  const getProviderSummary = (provider) => {
    return `
Name: ${provider["Company Name"]}
Location: ${provider.Location || "Location not specified"}
Website: ${provider.URL || "No website provided"}
Approved by Outdoorec: ${provider["Outdoorec Approved"] || "Not approved"}
    `.trim();
  };

  // Fetch providers from Supabase
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true);
        
        const { data, error: fetchError } = await supabase
          .from("providers")
          .select("*")
          .order("id", { ascending: true });

        if (fetchError) {
          throw fetchError;
        }

        setProviders(data || []);
        setError("");
      } catch (err) {
        console.error("Error fetching providers:", err);
        setError(`Failed to load providers: ${err.message || "Unknown error"}`);
        setProviders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, []);

  // Extract unique locations from providers and normalize them
  const uniqueLocations = React.useMemo(() => {
    const locationMap = new Map(); // normalized => original variations
    
    providers.forEach((p) => {
      const rawLocation = p["Location"] || p.location || "";
      if (rawLocation) {
        const normalized = normalizeLocation(rawLocation);
        if (!locationMap.has(normalized)) {
          locationMap.set(normalized, []);
        }
        locationMap.get(normalized).push(rawLocation);
      }
    });
    
    return Array.from(locationMap.keys()).sort((a, b) => a.localeCompare(b));
  }, [providers]);

  // Filter providers based on search term, approved filter, and location filter
  const filteredProviders = providers.filter((provider) => {
    // Handle both quoted column names and regular column names
    const companyName = provider["Company Name"] || provider.company_name || "";
    const location = provider["Location"] || provider.location || "";
    const approved = provider["Outdoorec Approved"] || provider.outdoorec_approved || "";
    
    // Normalize location for comparison
    const normalizedLocation = normalizeLocation(location);

    const matchesSearch =
      !searchTerm ||
      companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesApproved =
      !filterApproved || approved.toLowerCase() === "approved";

    const matchesLocation =
      selectedLocations.length === 0 || selectedLocations.includes(normalizedLocation);

    return matchesSearch && matchesApproved && matchesLocation;
  });

  return (
    <div className="w-full h-full overflow-auto" style={{ display: 'flex', flexDirection: 'column', padding: '16px', backgroundColor: '#1a1a1a', color: '#fff' }}>
      {/* Header */}
      <div className="mb-6">
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px', color: '#fff' }}>
          Outdoor Recreation Providers
        </h1>
        <p style={{ color: '#ccc' }}>
          A general directory of outdoor recreation service providers. Only providers marked as "Approved" are affiliated with Outdoorec. All other listings are publicly available information with no endorsement or association implied.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 flex flex-col gap-4">
        <div className="relative">
          <FaSearch className="absolute left-3 top-3" style={{ color: '#999' }} />
          <input
            type="text"
            placeholder="Search by company name or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              paddingLeft: '40px',
              paddingRight: '16px',
              paddingTop: '8px',
              paddingBottom: '8px',
              borderRadius: '4px',
              border: '1px solid #444',
              backgroundColor: '#2a2a2a',
              color: '#fff',
              outline: 'none'
            }}
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filterApproved}
            onChange={(e) => setFilterApproved(e.target.checked)}
            style={{ width: '16px', height: '16px' }}
          />
          <span style={{ color: '#ccc', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaCheckCircle style={{ color: '#4caf50' }} />
            Outdoorec Approved Only
          </span>
        </label>

        {/* Location Filter */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowLocationDropdown(!showLocationDropdown)}
            style={{
              width: '100%',
              padding: '8px 16px',
              backgroundColor: '#2a2a2a',
              color: '#fff',
              border: '1px solid #444',
              borderRadius: '4px',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span>
              {selectedLocations.length === 0
                ? 'Filter by Location'
                : `${selectedLocations.length} location${selectedLocations.length !== 1 ? 's' : ''} selected`}
            </span>
            <span style={{ fontSize: '12px' }}>{showLocationDropdown ? '▲' : '▼'}</span>
          </button>

          {/* Dropdown Menu */}
          {showLocationDropdown && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#2a2a2a',
                border: '1px solid #444',
                borderTop: 'none',
                borderRadius: '0 0 4px 4px',
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 10,
                marginTop: '-1px'
              }}
            >
              {/* Select All / Clear All buttons */}
              <div style={{ display: 'flex', gap: '8px', padding: '8px', borderBottom: '1px solid #444' }}>
                <button
                  onClick={() => setSelectedLocations([...uniqueLocations])}
                  style={{
                    flex: 1,
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: '#1976d2',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer'
                  }}
                >
                  Select All
                </button>
                <button
                  onClick={() => setSelectedLocations([])}
                  style={{
                    flex: 1,
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: '#666',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
              </div>

              {/* Location Options */}
              {uniqueLocations.map((location) => {
                const countInLocation = providers.filter(
                  (p) => normalizeLocation(p["Location"] || p.location) === location
                ).length;
                
                return (
                  <label
                    key={location}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      backgroundColor: selectedLocations.includes(location) ? '#333' : '#2a2a2a',
                      borderBottom: '1px solid #444'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#333')}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = selectedLocations.includes(location)
                        ? '#333'
                        : '#2a2a2a')
                    }
                  >
                    <input
                      type="checkbox"
                      checked={selectedLocations.includes(location)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLocations([...selectedLocations, location]);
                        } else {
                          setSelectedLocations(selectedLocations.filter((l) => l !== location));
                        }
                      }}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ color: '#ccc', fontSize: '14px' }}>
                      {location} ({countInLocation})
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#8b4545', color: '#fff', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '48px', paddingBottom: '48px' }}>
          <div style={{ animation: 'spin 1s linear infinite', borderRadius: '50%', height: '48px', width: '48px', borderBottom: '2px solid #2196f3' }}></div>
        </div>
      )}

      {/* Results Count */}
      {!loading && (
        <div style={{ marginBottom: '16px', fontSize: '14px', color: '#999' }}>
          Showing {filteredProviders.length} of {providers.length} providers
        </div>
      )}

      {/* Providers List - Scrollable */}
      <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}>
        {/* Providers List */}
        {!loading && filteredProviders.length > 0 && (
          <div className="grid gap-4">
          {filteredProviders.map((provider, index) => {
            const companyName = provider["Company Name"] || provider.company_name || "Unnamed Provider";
            const url = provider["URL"] || provider.url || "";
            const location = provider["Location"] || provider.location || "";
            const approved = provider["Outdoorec Approved"] || provider.outdoorec_approved || "";

            return (
              <div
                key={index}
                style={{
                  padding: '16px',
                  backgroundColor: '#2a2a2a',
                  borderRadius: '4px',
                  border: '1px solid #444',
                  transition: 'all 0.3s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.5)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff', marginBottom: '4px' }}>
                      {companyName}
                    </h3>
                    {location && (
                      <p style={{ fontSize: '14px', color: '#999' }}>
                        📍 {getLocationDisplayName(location)}
                      </p>
                    )}
                  </div>
                  {approved.toLowerCase() === "approved" && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#1b5e20', color: '#4caf50', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', marginLeft: '8px' }}>
                      <FaCheckCircle /> Approved
                    </div>
                  )}
                </div>

                {/* Add to Field Notes Checkbox */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    id={`add-notes-${index}`}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const providerSummary = `Outdoor recreation provider at ${provider.Location || "Location not specified"}. Website: ${provider.URL || "Not provided"}`;
                        setGroqContext(providerSummary);
                        setLocation({ name: "", lat: undefined, lon: undefined });
                        groqDrawerOpenRef.current?.();
                      }
                    }}
                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                  />
                  <label htmlFor={`add-notes-${index}`} style={{ cursor: 'pointer', fontSize: '14px', color: '#999' }}>
                    Add to Field Notes
                  </label>
                </div>

                {/* URL Link */}
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#64b5f6', textDecoration: 'none', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#42a5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#64b5f6'}
                  >
                    Visit Website
                    <FaExternalLinkAlt style={{ width: '12px', height: '12px' }} />
                  </a>
                )}

                {/* Additional Info */}
                {!url && (
                  <p style={{ fontSize: '12px', color: '#666', fontStyle: 'italic', marginBottom: '8px' }}>
                    No website available
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredProviders.length === 0 && (
        <div style={{ textAlign: 'center', paddingTop: '48px', paddingBottom: '48px' }}>
          <p style={{ color: '#999', marginBottom: '8px' }}>
            {searchTerm || filterApproved
              ? "No providers found matching your filters"
              : "No providers available"}
          </p>
          {(searchTerm || filterApproved || selectedLocations.length > 0) && (
            <button
              onClick={() => {
                setSearchTerm("");
                setFilterApproved(false);
                setSelectedLocations([]);
                setShowLocationDropdown(false);
              }}
              style={{
                color: '#64b5f6',
                fontSize: '14px',
                fontWeight: '500',
                border: 'none',
                background: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#42a5f5'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#64b5f6'}
            >
              Clear filters
            </button>
          )}
        </div>
      )}
      </div>
      
      {/* YOGi Drawer */}
      <GroqDrawerTrigger
        onOpen={(fn) => (groqDrawerOpenRef.current = fn)}
        setQuery={(fn) => (groqSetQueryRef.current = fn)}
        systemPromptKey="blm_map_system_prompt"
      />
    </div>
  );
};

export default ProvidersPage;
